import fs from "node:fs";
import path from "node:path";
import { downloadImage, getFilenameFromUrl, success } from "@iflyrpa/share";
import FormData from "form-data";
import type { Weixin } from "../../types";
import type { AspectRatio } from "../../utils";
import { calculateCropCoordinates, getFileState } from "../../utils";
import { Http } from "../../utils/http";
import type { PublishAction, WeixinmpPublishParams } from ".";

type ColorResponse = {
    width: number;
    height: number;
    theme_color: { r: number; g: number; b: number };
};
type ShareInamgeInfo = {
    cdn_url: string;
    disable_live: 0;
    disable_theme: 1;
    file_id: number;
    width: number;
    height: number;
    theme_color: string;
    url: string;
};

const saveDraft = (http: Http, params: WeixinmpPublishParams, data: Record<string, unknown>) => {
    const formData = new FormData();

    for (const [key, value] of Object.entries(data)) {
        formData.append(key, value);
    }

    return http.api({
        method: "post",
        url: "https://mp.weixin.qq.com/cgi-bin/operate_appmsg",
        params: {
            token: params.token,
            t: "ajax-response",
            sub: "update",
            type: 77,
            lang: "zh_CN",
        },
        data: formData,
    });
};

export const mockAction: PublishAction = async (task, params) => {
    const tmpCachePath = task.getTmpPath();
    const http = new Http({
        headers: {
            cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
            origin: "https://mp.weixin.qq.com",
            referer: `https://mp.weixin.qq.com/cgi-bin/appmsg?token=${params.token}&lang=zh_CN`,
        },
    });

    // 1. 上传图片
    const shareImageInfos: ShareInamgeInfo[] = [];
    const cropResult: Record<string, string> = {};

    if (params.banners && params.banners.length > 0) {
        // 下载图片到本地
        const images = await Promise.all(
            params.banners.map((url) => {
                const fileName = getFilenameFromUrl(url);
                return downloadImage(url, path.join(tmpCachePath, fileName));
            }),
        );

        for (let index = 0; index < images.length; index++) {
            const image = images[index];
            const fileState = getFileState(image);

            const formData = new FormData();
            formData.append("id", `WU_FILE_${index + 1}`);
            formData.append("name", fileState.name);
            formData.append("type", fileState.type);
            formData.append("lastModifiedDate", fileState.lastModifiedDate);
            formData.append("size", fileState.size);
            formData.append("file", fs.createReadStream(image));

            const response = await http.api<{ cdn_url: string; content: string }>({
                method: "post",
                url: `https://mp.weixin.qq.com/cgi-bin/filetransfer?action=upload_material&f=json&writetype=doublewrite&token=${params.token}&lang=zh_CN`,
                data: formData,
            });

            // 获取图片的主题色
            const colorResponseData = await http.api<ColorResponse>({
                method: "get",
                url: "https://mp.weixin.qq.com/cgi-bin/filepage",
                params: {
                    action: "getimgext",
                    fileid: response.content,
                    token: params.token,
                    lang: "zh_CN",
                    f: "json",
                    ajax: 1,
                },
            });

            const themeColor = colorResponseData.theme_color;

            shareImageInfos.push({
                cdn_url: response.cdn_url,
                disable_live: 0,
                disable_theme: 1,
                file_id: Number(response.content),
                width: colorResponseData.width,
                height: colorResponseData.height,
                theme_color: `rgb(${themeColor.r},${themeColor.g},${themeColor.b})`,
                url: response.cdn_url,
            });

            // 第一张图片还需要添加头图裁切
            if (index === 0) {
                const cropFormData = new FormData();
                cropFormData.append("imgurl", response.cdn_url);

                // 需要裁切 16:9，3:4，1:1 三种尺寸的图片
                const cropRatios: AspectRatio[] = [
                    [16, 9],
                    [1, 1],
                    [3, 4],
                ];
                cropFormData.append("size_count", cropRatios.length);
                const cropCoordinates = cropRatios.map((r, idx) => {
                    const cropCoordinate = calculateCropCoordinates(
                        colorResponseData.width,
                        colorResponseData.height,
                        r,
                    );
                    cropFormData.append(`size${idx}_x1`, cropCoordinate.x1);
                    cropFormData.append(`size${idx}_y1`, cropCoordinate.y1);
                    cropFormData.append(`size${idx}_x2`, cropCoordinate.x2);
                    cropFormData.append(`size${idx}_y2`, cropCoordinate.y2);

                    return { ratio: `${r[0]}_${r[1]}`, ...cropCoordinate };
                });

                cropFormData.append("token", params.token);
                cropFormData.append("lang", "zh_CN");
                cropFormData.append("f", "json");
                cropFormData.append("ajax", 1);

                const cropResponse = await http.api<{ result: Weixin.WeiXinCropResult[] }>({
                    method: "post",
                    url: "https://mp.weixin.qq.com/cgi-bin/cropimage?action=crop_multi",
                    data: cropFormData,
                });

                cropResult.cdn_url_back0 = response.cdn_url; // 原图
                cropResult.cdn_url0 = cropResponse.result[0].cdnurl; // 16:9
                cropResult.cdn_235_1_url0 = cropResponse.result[0].cdnurl; // 16:9
                cropResult.cdn_16_9_url0 = cropResponse.result[0].cdnurl; // 16:9
                cropResult.cdn_1_1_url0 = cropResponse.result[1].cdnurl; // 1:1
                cropResult.cdn_3_4_url0 = cropResponse.result[2].cdnurl; // 3:4
                cropResult.crop_list0 = JSON.stringify({
                    crop_list: cropCoordinates.map((it, idx) => ({
                        file_id: cropResponse.result[idx].file_id,
                        ratio: it.ratio,
                        x1: 0,
                        x2: 0,
                        y1: 0,
                        y2: 0,
                    })),
                    crop_list_percent: cropCoordinates.map((it, idx) => ({
                        ...it,
                        file_id: cropResponse.result[idx].file_id,
                    })),
                });
            }
        }
    }

    // 保存
    const res = await saveDraft(http, params, {
        ...cropResult,
        token: params.token,
        lang: "zh_CN",
        f: "json",
        ajax: 1,
        operate_from: "Chrome",
        title0: params.title,
        content0: params.content,
        share_imageinfo0: JSON.stringify(shareImageInfos),
    });

    return success(res);
};
